import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutePaths } from '../../../Constants/Common';
import FormInput from '../FormInput';
import PageTitle from '../PageTitle';
import TextInput from '../textInput/TextInput';
import SelectTitleInput from '../title/SelectTitleInput';
import ImageUploader from '../fileUpload/ImageUploader';
import {
    UploadedImage,
    CustomerName,
    EditNameEmailContent,
    EmailResponse,
    ApiFile
} from '../CommonTypes';
import ErrorSummary from '../ErrorSummary/ErrorSummary';
import { EmailApi } from '../../../api/EmailApi';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAppSelector } from 'state/hooks';
import { useTranslation } from 'react-i18next';

type PageErrors = {
    titleError: string | undefined;
    firstNameError: string | undefined;
    middleNameError: string | undefined;
    lastNameError: string | undefined;
    requiredDocumentError: string | undefined;
};

const AmendPersonalDetailsPage = (): JSX.Element => {
    const customer = useAppSelector((state) => state.customer); // consider more performant hook usage
    const vehicles = useAppSelector((s) => s.vehicles.vehicleList);
    const { t } = useTranslation();

    const navigate = useNavigate();

    //if (!customer) return <></>;

    const [title, setTitle] = useState(customer.title);
    const [firstName, setFirstName] = useState(customer.firstName);
    const [middleName, setMiddleName] = useState(customer.middleName);
    const [lastName, setLastName] = useState(customer.lastName);
    const [requiredDocument, setRequiredDocument] = useState<UploadedImage>(
        customer.supportingDocument1
    );
    const [optionalDocument, setOptionalDocument] = useState<UploadedImage>(
        customer.supportingDocument2
    );
    const [pageErrors, setPageErrors] = useState<PageErrors>();

    const { getAccessTokenSilently } = useAuth0();

    const originalCustomerName: CustomerName = {
        title: customer.title,
        firstName: customer.firstName,
        middleName: customer.middleName,
        lastName: customer.lastName
    };

    const logEmailError = (errors: string[]) => {
        console.log('Failed to send email');

        errors.forEach((error) => console.log(error));
    };

    const sendEmail = () => {
        const sendEditNameEmail = async () => {
            const controller = new AbortController();

            const sendEmailSuccess = (response: EmailResponse) => {
                if (response.success) {
                    toast.success(t('send_email_toast_successmsg'), {
                        onClose: () => {
                            navigate(RoutePaths.PersonalDetails);
                        }
                    });
                } else {
                    logEmailError(response.errorMessages);
                }
            };

            const sendEmailFailure = (response: EmailResponse) => {
                logEmailError(response.errorMessages);
            };

            const token = await getAccessTokenSilently();

            const requiredFile: ApiFile = {
                filename: requiredDocument?.filename ?? '',
                filepath: requiredDocument?.uploadedPath ?? '',
                filetype: requiredDocument?.type ?? ''
            };

            const optionalFile: ApiFile = {
                filename: optionalDocument?.filename ?? '',
                filepath: optionalDocument?.uploadedPath ?? '',
                filetype: optionalDocument?.type ?? ''
            };

            const data: EditNameEmailContent = {
                originalName: originalCustomerName,
                policies: vehicles.flatMap((a) => a.policies),
                editedName: {
                    title: title,
                    firstName: firstName,
                    middleName: middleName,
                    lastName: lastName
                },
                dateRequested: new Date().toString(),
                requiredFile: requiredFile,
                optionalFile: optionalFile
            };

            await new EmailApi().SendEditNameEmailAsync(
                data,
                token,
                sendEmailSuccess,
                sendEmailFailure,
                controller.signal
            );

            return () => {
                controller.abort();
            };
        };

        sendEditNameEmail();
    };

    const updateUserDetails = (
        event: React.FormEvent<HTMLInputElement>,
        key: string
    ) => {
        const value = event.currentTarget.value;

        switch (key) {
            case 'firstName':
                setFirstName(value);
                break;
            case 'middleName':
                setMiddleName(value);
                break;
            case 'lastName':
                setLastName(value);
                break;
            default:
                break;
        }
    };

    const onSelectTitle = (selectedValue: string) => {
        setTitle(selectedValue);
    };

    const onCancelClicked = () => {
        navigate(RoutePaths.PersonalDetails);
    };

    const onSubmit = () => {
        if (validateInputs()) {
            sendEmail();
        }
    };

    const validateInputs = (): boolean => {
        const validationErrors: PageErrors = {
            titleError: undefined,
            firstNameError: undefined,
            middleNameError: undefined,
            lastNameError: undefined,
            requiredDocumentError: undefined
        };

        if (!title) validationErrors.titleError = t('title_is_required');

        if (!firstName)
            validationErrors.firstNameError = t('first_name_is_required');
        else if (firstName.length > 100)
            validationErrors.firstNameError = t(
                'first_name_should_not_be_greater_than_100_characters'
            );

        if (middleName && middleName.length > 100)
            validationErrors.middleNameError = t(
                'middle_name_should_not_be_greater_than_100_characters'
            );

        if (!lastName)
            validationErrors.lastNameError = t('last_name_is_required');
        else if (lastName.length > 100)
            validationErrors.lastNameError = t(
                'last_name_should_not_be_greater_than_100_characters'
            );

        if (!requiredDocument)
            validationErrors.requiredDocumentError = t(
                'supporting_document_is_required'
            );

        setPageErrors(validationErrors);

        const isPageValid = Object.values(validationErrors).every(
            (el) => el === undefined
        );

        return isPageValid;
    };

    const getErrors = () => {
        const errors = [];

        if (pageErrors) {
            pageErrors.titleError && errors.push(pageErrors.titleError);
            pageErrors.firstNameError && errors.push(pageErrors.firstNameError);
            pageErrors.lastNameError && errors.push(pageErrors.lastNameError);
            pageErrors.requiredDocumentError &&
                errors.push(pageErrors.requiredDocumentError);
        }

        return errors;
    };

    const onRequiredDocumentUploaded = (
        type: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => {
        setRequiredDocument({ type, uploadedPath, imageId, image, filename });
    };

    const onOptionalDocumentUploaded = (
        type: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => {
        setOptionalDocument({ type, uploadedPath, imageId, image, filename });
    };

    const helpText = t('personal_details_help_text');

    return (
        <div className='card page-content'>
            <PageTitle title={t('amend_details_title')} />
            <FormInput
                id='amendpolicyholderinfo'
                label={t('policy_holder_details_label')}>
                <FormInput
                    id='titleInput'
                    label={t('title')}
                    errorText={pageErrors?.titleError}>
                    <div id='title-select' className='col-auto'>
                        <SelectTitleInput
                            displayLabel=''
                            selectedTitle={title}
                            onSelectTitle={onSelectTitle}
                        />
                    </div>
                </FormInput>
                <FormInput
                    id='firstNameInput'
                    label={t('first_name')}
                    errorText={pageErrors?.firstNameError}>
                    <div className='row repair-request-field-container'>
                        <div className='col-xs-12 col-lg-6'>
                            <TextInput
                                name='firstName'
                                value={firstName}
                                onChange={(e) =>
                                    updateUserDetails(e, 'firstName')
                                }
                                placeholder={t('first_name')}
                            />
                        </div>
                    </div>
                </FormInput>
                <FormInput id='middleNameInput' label={t('middle_name')}>
                    <div className='row repair-request-field-container'>
                        <div className='col-xs-12 col-lg-6'>
                            <TextInput
                                name='middleName'
                                value={middleName}
                                onChange={(e) =>
                                    updateUserDetails(e, 'middleName')
                                }
                                placeholder={t('middle_name')}
                            />
                        </div>
                    </div>
                </FormInput>
                <FormInput
                    id='lastNameInput'
                    label={t('last_name')}
                    errorText={pageErrors?.lastNameError}>
                    <div className='row repair-request-field-container'>
                        <div className='col-xs-12 col-lg-6'>
                            <TextInput
                                name='lastName'
                                value={lastName}
                                onChange={(e) =>
                                    updateUserDetails(e, 'lastName')
                                }
                                placeholder={t('last_name')}
                            />
                        </div>
                    </div>
                </FormInput>
            </FormInput>
            <FormInput
                id='supportingDocumentFormInput'
                label={t('supporting_documents_field_label')}
                errorText={pageErrors?.requiredDocumentError}
                helpText={helpText}>
                <div className='images-wrapper'>
                    <div className='image-wrapper'>
                        <div className='image-preview'>
                            <div className='image-preview-header'>
                                {t('supporting_document_1_header')}
                            </div>
                            <div
                                className='image-preview-content'
                                id='requiredDocumentUploader'>
                                <ImageUploader
                                    name='additional-image-preview'
                                    title={t('upload_image')}
                                    allowPdf={true}
                                    saveOnUpload={false}
                                    UploadedImage={requiredDocument}
                                    onImageChange={onRequiredDocumentUploaded}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='image-wrapper'>
                        <div className='image-preview'>
                            <div className='image-preview-header'>
                                {t('supporting_document_2_header')}
                            </div>
                            <div
                                className='image-preview-content'
                                id='optionalDocumentUploader'>
                                <ImageUploader
                                    name='additional-image-preview'
                                    title={t('upload_image')}
                                    allowPdf={true}
                                    saveOnUpload={false}
                                    UploadedImage={optionalDocument}
                                    onImageChange={onOptionalDocumentUploaded}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </FormInput>

            <br />
            <div className='bottom-navigation'>
                <button
                    name='btnSubmit'
                    data-testid='primary-button'
                    className='btn btn-primary progress-button'
                    onClick={onSubmit}>
                    {t('submit')}
                </button>
                <button
                    name='btnCancel'
                    data-testid='secondary-button'
                    className='btn btn-secondary progress-button'
                    onClick={onCancelClicked}>
                    {t('cancel')}
                </button>
            </div>
            <div className='card-footer page-content-footer'>
                <ErrorSummary
                    title={t('page_errors')}
                    errors={getErrors()}
                    className='card-error'
                />
            </div>
        </div>
    );
};

export default AmendPersonalDetailsPage;
